import React from 'react';
import { Text } from '@fragments';
import Parser from '@helpers/parser';
import {
    ContentCopyRounded,
    AccessTimeRounded,
    OpenInNewRounded,
    SellRounded,
    PersonRounded,
    KeyboardReturnRounded,
} from '@mui/icons-material';
import { getChargebackReason } from '../../../enums/chargebackReasons';
import './styles.scss';

function CopyIcon({ value }) {
    if (!value) return null;
    return (
        <ContentCopyRounded
            className="sidebar-copy-btn"
            onClick={() => {
                navigator.clipboard.writeText(String(value)).catch(() => { });
                if (window.setSnackbar) window.setSnackbar('Copiado!');
            }}
            style={{ fontSize: 13 }}
        />
    );
}

function Section({ icon, title, children, animationDelay = 0 }) {
    return (
        <div className="sidebar-section" style={{ animationDelay: `${animationDelay}ms` }}>
            <Text.h4 weight={400} contrast={7} className="sidebar-section__title" left={icon}>
                {title}
            </Text.h4>
            <div className="sidebar-section__body">{children}</div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="sidebar-detail-row">
            <Text.span weight={400} contrast={6} className="sidebar-detail-row__label">
                {label}
            </Text.span>
            <div className="sidebar-detail-row__value">{children}</div>
        </div>
    );
}

function Value({ children, left, copy, className, ...props }) {
    return (
        <div className={`sidebar-detail-value ${className || ''}`}>
            {left && React.cloneElement(left, { style: { fontSize: 13, opacity: 0.6, ...left.props.style } })}
            <Text.span weight={300} contrast={5} {...props}>
                {children}
            </Text.span>
            {copy && <CopyIcon value={copy} />}
        </div>
    );
}

function formatDeadline(deadline) {
    if (!deadline) return '—';
    return new Date(deadline).toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

export default function DetailsSidebar({ chargeback }) {
    const reasonData = getChargebackReason(chargeback.reason);
    const deadline = chargeback.timestamps?.deadline;
    const hasDisputeDocuments = chargeback.dispute?.documents?.length > 0;
    const firstDocument = chargeback.dispute?.documents?.[0];

    const createdEvent = (chargeback.events || []).find(e => e.type === 'created');
    const providerChargebackId = createdEvent?.metadata?.id || null;

    return (
        <div className="details-sidebar">
            <Section icon={<KeyboardReturnRounded />} title="Contestação" animationDelay={100}>
                <Row label="ID da contestação">
                    <Value copy={chargeback.id}>
                        {`${chargeback.id}`}
                    </Value>
                </Row>

                <Row label="Valor">
                    <Value>
                        R$ {Parser.price.brl(chargeback.amount)}
                    </Value>
                </Row>

                <Row label="Prazo para resposta à contestação">
                    <Value left={<AccessTimeRounded />}>
                        {formatDeadline(deadline)}
                    </Value>
                </Row>

                {hasDisputeDocuments && (
                    <Row label="Documentos anexados">
                        {
                            chargeback.dispute?.documents?.map((document, index) => (
                                <a
                                    href={document.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sidebar-link"
                                >
                                    <Value left={<OpenInNewRounded />}>
                                        Baixar documento
                                    </Value>
                                </a>
                            ))
                        }
                    </Row>
                )}

                <Row label="Motivo">
                    <Value className="sidebar-reason-label">
                        {reasonData.label}
                    </Value>
                </Row>

                {chargeback.category && (
                    <Row label="Categoria">
                        <Value>{chargeback.category}</Value>
                    </Row>
                )}
            </Section>

            <div className="sidebar-divider" />

            <Section icon={<SellRounded />} title="Detalhes da cobrança" animationDelay={200}>
                <Row label="ID da cobrança">
                    <Value copy={chargeback.charge}>#{chargeback.charge}</Value>
                </Row>

                {chargeback.plan && (
                    <Row label="Plano">
                        <Value>{chargeback.plan.label}</Value>
                    </Row>
                )}

                {chargeback.plan?.interval && (
                    <Row label="Recorrência">
                        <Value>
                            {Parser.interval?.adjective(chargeback.plan.interval) || chargeback.plan.interval}
                        </Value>
                    </Row>
                )}
            </Section>

            <div className="sidebar-divider" />

            <Section icon={<PersonRounded />} title="Cliente" animationDelay={300}>
                {chargeback.user ? (
                    <>
                        <Row label="Nome">
                            <Value>
                                {chargeback.user.username || '—'}
                            </Value>
                        </Row>

                        <Row label="E-mail">
                            <Value copy={chargeback.user.email}>
                                {chargeback.user.email || '—'}
                            </Value>
                        </Row>

                        <Row label="Telefone">
                            <Value
                                left={<img src={`https://flagcdn.com/${Parser.getCountryIso(chargeback.user.phone)}.svg`} width="16" alt="" style={{ borderRadius: 2, transform: 'translateY(-1px)' }} />}
                                copy={Parser.phone(`${chargeback.user.phone.areaCode}${chargeback.user.phone.number}`)}
                            >
                                  {Parser.phone(`${chargeback.user.phone.areaCode}${chargeback.user.phone.number}`)}
                            </Value>
                        </Row>
                    </>
                ) : (
                    <Value>Informações do cliente indisponíveis</Value>
                )}
            </Section>
        </div>
    );
}

DetailsSidebar.Section = Section;
DetailsSidebar.Row = Row;
DetailsSidebar.Value = Value;
